import { APIService } from 'src/app/service/api.service';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { CookieService } from 'ngx-cookie';
import { Observable, of } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { failIfSwallowed } from '../util/swallowed-http';
import { NonMember, User, UserInfo, UserInfoUpdateDto } from './../model/user';
import { MessageService } from './message.service';

/** 로그아웃이 무응답으로 멈추는 것을 막는 상한. APIService는 timeout을 걸지 않는다. */
const LOGOUT_TIMEOUT_MS = 10_000;

// Account Linking Interfaces
export interface SocialAccountDto {
  id: number;
  provider: string;
  email?: string;
  displayName?: string;
  lastUsedAt?: Date;
  createdAt: Date;
}

export interface MatchingAccountDto {
  id: number;
  email: string;
  nickname: string;
  profilePhoto?: string;
  phoneNumber?: string;
  provider?: string;
  hasPassword: boolean;
  lastLoginAt?: Date;
  socialAccounts: SocialAccountDto[];
}

export interface LinkAccountInfoDto {
  provider: string;
  email: string;
  displayName?: string;
  profileImage?: string;
  matchingAccounts: MatchingAccountDto[];
  /** Token expiration time (ISO 8601) */
  expiresAt: string;
}

export interface SocialAccountsResponseDto {
  connected: SocialAccountDto[];
  available: string[];
  hasPassword: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  ACCESS_TOKEN = 'access_token';

  constructor(
    private apiService: APIService,
    private router: Router,
    private jwtHelper: JwtHelperService,
    private cookieService: CookieService,
    private messageService: MessageService,
  ) {}

  getCurrentUserFromServer(): Observable<User> {
    return this.apiService.get<User>(`/api/users/user/current`);
  }

  getCurrentUserFromServerWithUserInfo(): Observable<User> {
    return this.apiService.get<User>(`/api/users/user/current/userInfo`);
  }

  getCurrentUser(): Observable<User> {
    const user: User = this.jwtHelper.decodeToken(this.getToken());
    if (!user) return of(undefined);
    return of(user);
  }

  updateUserInfo(userInfoUpdateDto: UserInfoUpdateDto): Observable<UserInfo> {
    return this.apiService.put<UserInfo>('/api/users/userInfo', userInfoUpdateDto);
  }

  requestAuthSms(phoneNumber: string): Observable<boolean> {
    return this.apiService.post<boolean>(`/auth/sms`, {
      phoneNumber: phoneNumber,
    });
  }

  checkAuthSms(phoneNumber: string, authNumber: string): Observable<boolean> {
    return this.apiService.post<boolean>(`/auth/sms/check`, {
      phoneNumber: phoneNumber,
      authNumber: authNumber,
    });
  }

  login(email: string, password: string) {
    return this.apiService.post('/auth/login', { email, password }, { withCredentials: true });
  }

  async logout() {
    // access_token 쿠키는 서버 응답의 Set-Cookie로만 지워진다. 따라서 요청이 실제로
    // 성공했는지 확실히 알아야 한다.
    // - 502는 ErrorHttpInterceptor가 EMPTY로 삼키는데, 빈 스트림의 toPromise()는
    //   reject가 아니라 undefined로 **resolve** 한다. 그대로 두면 실패가 성공으로
    //   둔갑해 쿠키가 남은 채 로그인 화면으로 보내지고, 사용자는 되돌아온다.
    // - 무응답으로 매달리면 호출부가 영원히 대기 상태에 갇힌다.
    await this.apiService
      .post('/auth/logout', {}, { withCredentials: true })
      .pipe(timeout(LOGOUT_TIMEOUT_MS), failIfSwallowed('logout response swallowed by interceptor'))
      .toPromise();

    const userAgent = this.cookieService.get('nnb_platform');
    if (userAgent == 'NNB-ANDROID' || userAgent == 'NNB-IOS') {
      window.location.href = `/app/logout`;
    } else {
      window.location.href = `/tabs/login`;
    }
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    return token ? !this.jwtHelper.isTokenExpired(token) : false;
  }

  getToken(): string {
    return this.cookieService.get(this.ACCESS_TOKEN);
  }

  updateToken(id: number): Observable<any> {
    return this.apiService.post(`/auth/token/${id}`, {}, { withCredentials: true });
  }

  toastNeedLogin() {
    this.messageService.popup({
      title: '노는법 로그인',
      description: '로그인이 필요합니다.',
      cancelLabel: '확인',
    });
    this.router.navigate(['/tabs/login'], {
      queryParams: { returnUrl: this.router.url },
    });
  }

  // Account Linking Methods
  getLinkAccountInfo(token: string): Observable<LinkAccountInfoDto> {
    return this.apiService.get<LinkAccountInfoDto>(`/auth/link-account?token=${encodeURIComponent(token)}`);
  }

  linkAccount(
    token: string,
    selectedUserId: number,
    password?: string,
  ): Observable<{ isMember: boolean; jwt: string }> {
    return this.apiService.post<{ isMember: boolean; jwt: string }>(
      '/auth/link-account',
      { token, selectedUserId, password },
      { withCredentials: true },
    );
  }

  createFromLink(token: string): Observable<{ isMember: boolean; jwt: string }> {
    return this.apiService.post<{ isMember: boolean; jwt: string }>(
      '/auth/create-from-link',
      { token },
      { withCredentials: true },
    );
  }

  getSocialAccounts(): Observable<SocialAccountsResponseDto> {
    return this.apiService.get<SocialAccountsResponseDto>('/auth/social-accounts');
  }

  unlinkSocialAccount(socialAccountId: number): Observable<{ success: boolean }> {
    return this.apiService.delete<{ success: boolean }>(`/auth/social-accounts/${socialAccountId}`);
  }
}
